import { useQuery } from '@tanstack/react-query';
import { Navigate } from '@tanstack/react-router';

import { meQuery } from '../api/me.ts';
import { PageHeader } from '../components/page-header.tsx';
import { LeaveToApprove } from './crew-time-off.tsx';
import { HeldDaysToAssign } from './held-days-to-assign.tsx';
import { OffersOpen } from './offers-open.tsx';
import { ShiftsToConfirm } from './shifts-to-confirm.tsx';
import { DocksToBook } from './venue-warnings.tsx';

// What /v1/me returns, rendered. It is the smallest honest first screen: it proves the whole chain
// — Cognito token, the guard, the second-factor check, the cross-tenant resolution, a tenant-scoped
// read under RLS, the §3.7 projection — and it claims nothing that is not built.
//
// The design's home is a role-aware dashboard (jobs in flight, crew on site, exceptions). None of
// those figures exist yet, and a dashboard of invented numbers is the promise on a screen that
// CB-REQ ADO-06 prohibits. The figures arrive here as the slices that produce them land.

export function HomeScreen() {
  // The same query the layout above already resolved, so this reads the cache rather than the API.
  const me = useQuery(meQuery);

  if (me.isPending) return <p className="text-muted">Loading…</p>;

  if (me.isError) {
    return (
      <p role="alert" className="alert">
        {me.error.message}
      </p>
    );
  }

  // CB-REQ CRW-08 at v1.964: someone whose only business here is their own shifts starts there.
  const caps = me.data.capabilities;
  if (caps.includes('shifts.own') && !caps.includes('jobs.view'))
    return <Navigate to="/my-shifts" />;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader kicker="Home" title={me.data.displayName} meta={me.data.email} />

      <dl className="stat-grid grid-cols-1 sm:grid-cols-3">
        <div className="stat">
          <dt className="kicker">Company</dt>
          <dd className="mt-1.5 text-lg font-extrabold">{me.data.tenant.name}</dd>
        </div>
        <div className="stat">
          <dt className="kicker">Time zone</dt>
          {/* Stated rather than assumed: every window and rendered time resolves against it, and a
              formatter that defaults the zone is prohibited (CB-CONV §3.2, §7). */}
          <dd className="mt-1.5 text-lg font-extrabold">{me.data.tenant.defaultTz}</dd>
        </div>
        <div className="stat">
          <dt className="kicker">Prices and costs</dt>
          {/* FND-08's price line, as the API computed it from the roles held. */}
          <dd className="mt-1.5 text-lg font-extrabold">
            {me.data.canSeePrices ? 'Visible to you' : 'Hidden from you'}
          </dd>
        </div>
      </dl>
      {/* VEN-02 at v1.973: the first real figure on home — docks still to book, from real jobs. */}
      {caps.includes('jobs.view') ? <DocksToBook /> : null}
      {/* CRW-16 at v1.978: accepted shifts the office should confirm now. */}
      {caps.includes('shifts.view') ? (
        <ShiftsToConfirm mayConfirm={caps.includes('shifts.manage')} />
      ) : null}
      {/* CRW-11 at v1.980: held days still without a job, inside the assign-by window. */}
      {caps.includes('shifts.view') ? (
        <HeldDaysToAssign
          mayManage={caps.includes('shifts.manage')}
          seesPrices={me.data.canSeePrices}
        />
      ) : null}
      {/* CRW-02 at v1.9106: leave asked for, to approve or decline. */}
      {caps.includes('leave.approve') ? <LeaveToApprove /> : null}
      {/* CRW-04 at v1.994: offers with places still taking answers. */}
      {caps.includes('shifts.view') ? (
        <OffersOpen mayManage={caps.includes('shifts.manage')} seesPrices={me.data.canSeePrices} />
      ) : null}
    </div>
  );
}
